import {Component, effect, signal, WritableSignal} from '@angular/core';
import {CommonModule, formatDate} from '@angular/common';
import {DatevAuthStatusService} from '../../services/datev-auth-status.service';
import {AuthStatusService} from "../../services/auth-status.service";
import {environment} from "../../../environments/environment";
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faSync} from '@fortawesome/free-solid-svg-icons';
import {NotificationService} from '../../services/notification.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {extractHttpErrorMessage} from '../http-error.util';

@Component({
    selector: 'datev-app-auth-status',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule],
    template: `
        <div class="status-container">
            <div class="status-indicator" (click)="showUserInfo(true)">
                <div class="spinner" *ngIf="userInfoLoading()"></div>
                <span class="status-text">{{ printAuthStatus() }}</span>
                <div class="status-dot"
                     [class.authenticated]="datevAuthStatusService.getAuthStatus()() && datevAuthStatusService.getAllApisAvailable()()"
                     [class.degraded]="datevAuthStatusService.getAuthStatus()() && !datevAuthStatusService.getAllApisAvailable()()"
                     [class.not-authenticated]="!datevAuthStatusService.getAuthStatus()()">
                </div>
            </div>
        </div>

        <!-- User Info Modal -->
        <div class="modal-overlay" *ngIf="_showUserInfo">
            <div class="modal-container">
                <div class="modal-header">
                    <h2>Benutzer Information</h2>
                    <button class="close-button" aria-label="Schließen" (click)="showUserInfo(false)">×</button>
                </div>

                <div class="modal-content" *ngIf="datevUserInfo() as info">
                    <div class="loading-message" *ngIf="userInfoLoading()">
                        <div class="spinner"></div>
                        <span>Lade Benutzerinformationen...</span>
                    </div>
                    
                    <div *ngIf="!userInfoLoading()">
                    <div class="info-row">
                        <span class="label">Name:</span>
                        <span class="value">{{ info.name }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Familienname:</span>
                        <span class="value">{{ info.family_name }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">E-Mail:</span>
                        <span class="value">{{ info.email }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">E-Mail verifiziert:</span>
                        <span class="value">{{ info.email_verified ? 'Ja' : 'Nein' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Business Kunde:</span>
                        <span class="value">{{ info.is_member ? 'Ja' : 'Nein' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Login ist gültig bis:</span>
                        <span class="value">
                            {{ loginExpiresAt() }}
                            <button
                                    class="refresh-button"
                                    (click)="refreshTokens()"
                                    [disabled]="isRefreshing()"
                                    title="Login verlängern">
                                <fa-icon
                                        [icon]="refreshIcon"
                                        [class.spinning]="isRefreshing()">
                                </fa-icon>
                            </button>
                        </span>
                    </div>
                    </div>
                </div>
                <div class="">
                    <strong>
                        Hier können Sie den Zugriff unseres Services auf Ihre DATEV Daten widerrufen:
                        <a href="https://apps.datev.de/tokrevui">Wiederrufen</a>
                    </strong>
                </div>


                <div class="modal-footer" *ngIf="!datevAuthStatusService.getAuthStatus()()">
                    <button class="login-button" (click)="onDatevLogin()">
                        Bei DATEV anmelden
                    </button>
                </div>
                <div class="modal-footer" *ngIf="datevAuthStatusService.getAuthStatus()()">
                    <button class="login-button" (click)="onDatevLogout()">
                        Bei DATEV abmelden
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .status-container {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .status-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px;
            cursor: pointer;
            transition: opacity 0.2s;
        }

        .status-indicator:hover {
            opacity: 0.8;
        }

        .status-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
        }

        .authenticated {
            background-color: #28a745;
            box-shadow: 0 0 8px rgba(40, 167, 69, 0.5);
        }

        .degraded {
            background-color: #ffc107;
            box-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
        }

        .not-authenticated {
            background-color: #dc3545;
            box-shadow: 0 0 8px rgba(220, 53, 69, 0.5);
        }

        .status-text {
            font-size: 14px;
            color: #333;
            margin-left: 8px;
        }

        .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            display: inline-block;
        }

        .loading-message {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 20px;
            text-align: center;
            color: #666;
            font-style: italic;
        }

        .loading-message .spinner {
            width: 20px;
            height: 20px;
        }

        .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .modal-header h2 {
            margin: 0;
        }

        .close-button {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
        }

        .info-row {
            display: flex;
            margin-bottom: 12px;
            padding: 8px;
            border-bottom: 1px solid #eee;
        }

        .label {
            flex: 1;
            font-weight: bold;
            color: #666;
        }

        .value {
            flex: 2;
            color: #333;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .refresh-button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 50%;
            color: #007bff;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
        }

        .refresh-button:hover:not(:disabled) {
            background-color: #f8f9fa;
            color: #0056b3;
        }

        .refresh-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .spinning {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        .modal-footer {
            margin-top: 20px;
            text-align: center;
        }

        .login-button {
            background-color: #007bff;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
        }

        .login-button:hover {
            background-color: #0056b3;
        }

        @media (max-width: 768px) {
            .status-container {
                flex-direction: column;
                align-items: flex-start;
            }
        }
    `]
})
export class DatevAuthStatusComponent {
    // todo redesign this componeent
    userInfoLoading = this.datevAuthStatusService.getUserInfoLoading();
    _showUserInfo = false;
    datevUserInfo = this.datevAuthStatusService.getUserInfo();
    private status2print: string = "";
    loginExpiresAt: WritableSignal<string> = signal("");
    isRefreshing = signal<boolean>(false);
    refreshIcon = faSync;

    constructor(
        public datevAuthStatusService: DatevAuthStatusService,
        private logleAuthStatus: AuthStatusService,
        private notificationService: NotificationService,
        private http: HttpClient
    ) {
        effect(() => {
            const isAuth = this.datevAuthStatusService.getAuthStatus()();
            const isLoading = this.userInfoLoading();
            
            if (isLoading) {
                this.status2print = "Lade...";
            } else if (isAuth) {
                const info = this.datevAuthStatusService.getUserInfo()();
                const fullName = info?.name || "";
                this.status2print = fullName + this.printTenant();
            } else {
                this.status2print = "Datev Status";
            }
        });
    }

    printAuthStatus(): string {
        return this.status2print;
    }

    private printTenant(): string {
        return "(Mandat: " + (this.logleAuthStatus.getUser()()?.client_id || "nicht ausgewählt") + ")";
    }

    onDatevLogin() {
        this.datevAuthStatusService.loginDatev();
    }

    onDatevLogout() {
        this.datevAuthStatusService.logoutDatev();
    }

    refreshTokens() {
        this.isRefreshing.set(true);
        this.http.get<{ success: boolean, message: string }>(`${environment.apiUrl}/datev/refresh-tokens`)
            .subscribe({
                next: (response) => {
                    this.isRefreshing.set(false);
                    if (response.success) {
                        this.notificationService.show(
                            response.message || 'Login erfolgreich verlängert',
                            'success'
                        );
                        // Refresh the expiration date after successful token refresh
                        this.loadTokenExpirationDate();
                    } else {
                        this.notificationService.show(
                            response.message || 'Fehler beim Verlängern des Logins',
                            'error'
                        );
                    }
                },
                error: (error: HttpErrorResponse) => {
                    this.isRefreshing.set(false);
                    this.notificationService.show(extractHttpErrorMessage(error), 'error');
                }
            });
    }

    public async showUserInfo(ok: boolean) {
        await this.loadTokenExpirationDate();
        this._showUserInfo = ok;
    }

    private async loadTokenExpirationDate() {
        try {
            const response = await fetch(`${environment.apiUrl}/datev/token-expiration-date`);
            if (response.ok) {
                const dateString = await response.text();
                const formattedDate = formatDate(dateString, "dd.MM.yyyy HH:mm", "en-US");
                this.loginExpiresAt.set(formattedDate);
            }
        } catch (error) {
            console.error('Error loading token expiration date:', error);
        }
    }
}